import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Api, type MyProfile, type SuOrgRow } from './api';
import { AUTO, SERVER } from './config';
import { loadSavedLogin, persistRefreshToken, saveLastLoginId } from './appShell/loginMemory';
import { applyMainLayout, MAIN_LAYOUT } from './appShell/mainWindow';
import { confirmAction } from './ui/confirm';
import { isSameDeviceCode, SELF_CONNECT_BLOCKED, useMyDeviceCode } from './views/myConnection/useMyDeviceCode';
import { LoginView } from './views/LoginView';
import { ConnectView } from './views/ConnectView';
import { DevicesView } from './views/DevicesView';
import { SessionHistoryView } from './views/SessionHistoryView';
import { UsersView } from './views/UsersView';
import { AccessLogView } from './views/AccessLogView';
import { ROLE, ROLE_LABEL } from './admin/labels';
import { UpdateBanner } from './views/UpdateBanner';
import { PermissionBanner } from './views/myConnection/PermissionBanner';
import { checkForUpdate, type AvailableUpdate } from './update';
import { useSessionManager } from './sessions/useSessionManager';
import { SessionTabs } from './sessions/SessionTabs';
import {
  openInSessionWindow,
  reopenInFreshSessionWindow,
  subscribeLiveSessionTotal,
  type SessionOpenFailure,
} from './sessions/bridge';
import { SessionOpenAlert } from './sessions/SessionOpenAlert';
import { serveSessionTokens } from './sessions/tokenBroker';
import { MyAccountDialog } from './views/account/MyAccountDialog';
import { isTabLive, requestLabel, type ConnectOptions, type SessionRequest } from './sessions/types';
import { SuDashboardView } from './views/su/SuDashboardView';
import { SuOrgsView } from './views/su/SuOrgsView';
import { SuServerView } from './views/su/SuServerView';

type Nav = 'connect' | 'devices' | 'history' | 'users' | 'logs' | SuNav;
/** 수퍼유저 "전체" 모드 메뉴(§14.19.1) — 조직을 고르지 않았을 때만 보인다. */
type SuNav = 'su-dashboard' | 'su-orgs' | 'su-server';

/**
 * 사이드바 메뉴 — **데이터로** 정의한다(메뉴를 늘릴 때 JSX 를 복사하지 않게).
 * `adminOnly` 는 관리자에게만 보인다(2026-09-15 관리 화면). 비관리자에게는 "내 접속 이력" 만 추가로 보인다.
 * ⚠️ 숨김은 편의일 뿐 보안이 아니다 — 권한 검사는 서버가 한다(비관리자가 관리 API 를 부르면 403).
 */
const NAV_ITEMS: ReadonlyArray<{ key: Nav; label: string; adminLabel?: string; adminOnly?: boolean }> = [
  { key: 'connect', label: '연결' },
  { key: 'devices', label: '기기' },
  { key: 'history', label: '내 접속 이력', adminLabel: '접속 이력' },
  { key: 'users', label: '사용자', adminOnly: true },
  { key: 'logs', label: '감사 로그', adminOnly: true },
];

/**
 * 수퍼유저 "전체" 모드 메뉴(§14.19.1). 조직을 고르면 위 `NAV_ITEMS`(관리자 메뉴 포함)로 바뀐다 —
 * su 는 조직 안에서 그 조직 관리자와 같은 화면을 쓴다(사용자·기기·권한·이력).
 */
const SU_NAV_ITEMS: ReadonlyArray<{ key: SuNav; label: string }> = [
  { key: 'su-dashboard', label: '대시보드' },
  { key: 'su-orgs', label: '조직' },
  { key: 'su-server', label: '서버' },
];

/** 관리자 전용 메뉴 묶음 위에 붙는 머리글. */
const ADMIN_SECTION_LABEL = '관리';

/** 수퍼유저 조직 선택의 "전체" 항목 값(= 조직 문맥 없음). */
const SU_ALL_ORGS = '';

/** 기동 후 업데이트 확인까지의 지연(ms) — 창이 뜨는 경로에 네트워크 왕복을 얹지 않기 위한 여유. */
const UPDATE_CHECK_DELAY_MS = 3000;

/** "세션 창에서 열렸습니다" 안내를 보여 주는 시간(ms). */
const HANDOFF_NOTICE_MS = 5000;

/**
 * 로그인 사용자 요청이면 토큰을 **지금 쓸 수 있는 것**으로 바꾼 요청(계정 없는 접속은 그대로).
 * 세션 창은 받은 토큰으로 자기 소켓을 열 뿐 갱신하지 못하므로, 넘기기 직전에 여기서 새것으로 채운다.
 */
function withFreshToken(api: Api, req: SessionRequest): Promise<SessionRequest> {
  return req.kind === 'user' ? api.freshToken().then((token) => ({ ...req, token })) : Promise.resolve(req);
}

/**
 * 메인 창.
 *
 * 🗂 **원격 세션은 별도 창의 탭으로 연다**(TeamViewer 식, 2026-09-12 사용자 요청 — 종전엔 이 화면 전체가 세션 화면으로
 * 바뀌어 한 번에 한 기기만 볼 수 있었다). 메인 창은 목록·접속 폼을 그대로 유지하고, 세션 창(session.html)이 탭을 관리한다.
 *
 * 팝업이 차단됐거나(브라우저 설정) 자동 접속(`?auto=1`, 사용자 클릭이 없어 팝업 불가)이면 **이 창 안에서 같은 탭 화면**으로
 * 대신 연다(`inPage` — 세션 창과 같은 관리 로직). 기능이 조용히 막히는 것보다 같은 창에서라도 여는 것이 낫다.
 */
export function App() {
  const apiRef = useRef(new Api());
  const [authed, setAuthed] = useState(false);
  const [nav, setNav] = useState<Nav>('connect');
  const [error, setError] = useState('');
  // 이 PC 의 기기 코드(통합앱만) — 자기 자신에게 접속하는 것을 막는 데 쓴다(`startSession`).
  const myDeviceCode = useMyDeviceCode();
  // 로그인 화면으로 되돌려보낸 이유(자격 만료 등). 사용자가 다시 로그인하면 지운다.
  const [authNotice, setAuthNotice] = useState('');
  // 내 프로필 — **역할(role)로 관리 메뉴·관리 버튼을 보일지 정한다**(2026-09-15). 종전에는 화면이 역할을 몰라
  // 관리자 버튼을 모두에게 보여 주고 서버 403 에 기댔다. 불러오지 못하면(구버전 서버) null = 비관리자 취급.
  const [me, setMe] = useState<MyProfile | null>(null);
  // [내 계정] 창(사이드바 아래 이름을 누르면).
  const [accountOpen, setAccountOpen] = useState(false);
  // 수퍼유저(§14.19.1): 고른 조직(빈 값 = "전체" — su 화면). 조직을 고르면 그 조직 관리자와 같은 화면이 된다.
  const isSu = me?.isSu === true;
  const [suOrgId, setSuOrgId] = useState(SU_ALL_ORGS);
  const [suOrgs, setSuOrgs] = useState<SuOrgRow[]>([]);
  const inSuOrg = isSu && suOrgId !== SU_ALL_ORGS;
  const isAdmin = me?.role === ROLE.ADMIN || inSuOrg;
  // 세션 창으로 넘긴 직후의 짧은 안내("○○ — 원격 세션 창에서 열렸습니다").
  const [notice, setNotice] = useState('');
  // 세션 창으로 넘기기가 나중에 실패한 접속(통합앱) — 알림과 "다시 열기" 에 쓴다. 요청에 토큰이 있어 메모리에만 둔다.
  const [openFailure, setOpenFailure] = useState<{ req: SessionRequest; failure: SessionOpenFailure } | null>(null);
  // 대체 경로(같은 창) 세션 탭 + 그 탭 화면을 잠시 접어 두었는지.
  const inPage = useSessionManager();
  const [overlayHidden, setOverlayHidden] = useState(false);
  // 자동 업데이트(설계 §5): 확인 결과 + 사용자가 닫았는지. 닫으면 이번 실행 동안 다시 띄우지 않는다
  // (매번 다시 올라오면 알림이 아니라 방해가 된다 — 다음 기동에서 자연히 다시 확인한다).
  const [update, setUpdate] = useState<AvailableUpdate | null>(null);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  // 세션 창들(앱)의 진행 중 세션 합계 — 이 창 세션만 세면 세션 창에서 원격지원 중에도 "지금 업데이트" 가 떴다(2026-09-13 수정).
  const [windowLiveSessions, setWindowLiveSessions] = useState(0);
  useEffect(() => subscribeLiveSessionTotal(setWindowLiveSessions), []);

  const api = apiRef.current;
  const { open: openInPage, closeAll: closeAllInPage } = inPage;

  // 🔴 자격이 끊기면(접근 토큰 만료 + 갱신 실패) **말해 준다.** 종전에는 아무 안내 없이 모든 요청이
  //    401 로 떨어져 기기 목록이 통째로 비었고, 사용자는 "서버와 끊긴 건가" 로 읽었다(2026-09-14 실기).
  //    ⚠️ 진행 중인 원격 세션은 건드리지 않는다 — 세션 창은 자기 소켓으로 계속 살아 있다(로그아웃과 같은 원칙).
  useEffect(() => {
    api.onAuthLost = () => {
      setAuthed(false);
      setAuthNotice('로그인 유효 시간이 지났습니다. 다시 로그인해 주세요. 진행 중인 원격 세션은 그대로 유지됩니다.');
    };
    return () => {
      api.onAuthLost = undefined;
    };
  }, [api]);

  // 자동 로그인(통합앱 7일, 사용자 요청 2026-09-27 · 브라우저 = 탭을 닫을 때까지, 2026-10-12):
  // 로그인 유지 토큰이 바뀔 때마다 보관본을 최신으로(회전형 — `loginMemory.ts`).
  useEffect(() => {
    api.onRefreshTokenChanged = (rt) => persistRefreshToken(SERVER, rt);
    return () => {
      api.onRefreshTokenChanged = undefined;
    };
  }, [api]);

  // 세션 창이 [다시 연결]·탭 옮기기 때 새 접근 토큰을 물어 온다(sessions/tokenBroker.ts — 갱신 토큰은 이 창만 갖는다).
  // 로그인 상태가 아니면 빈 값 → 세션 창은 "로그인이 만료되어…" 로 안내한다.
  useEffect(() => serveSessionTokens(() => (api.isAuthed ? api.freshToken() : Promise.resolve(''))), [api]);

  // 켜질 때 한 번(브라우저는 새로고침마다) — 보관본이 있으면 조용히 되살린다. 검증용 자동 로그인(?auto=1)이 있으면 그쪽이 우선.
  // `resuming` 동안은 로그인 화면 대신 짧은 안내를 보인다(로그인 폼이 잠깐 번쩍이지 않게).
  const [resuming, setResuming] = useState(false);
  useEffect(() => {
    if (AUTO.auto) return;
    let alive = true;
    void (async () => {
      const saved = await loadSavedLogin();
      if (!saved || !alive) return;
      if (saved.server !== SERVER) {
        persistRefreshToken(SERVER, ''); // 다른 서버의 토큰은 이 서버에 보내지 않고 버린다
        return;
      }
      setResuming(true);
      const ok = await api.resume(saved.refreshToken);
      if (!alive) return;
      setResuming(false);
      if (ok) setAuthed(true);
    })();
    return () => {
      alive = false;
    };
  }, [api]);

  // 메인 창 크기(통합앱): 로그인 전 = 첫 화면 카드에 맞춘 작은 창, 로그인 후 = 목록·관리 화면용 넓은 창.
  // 로그인·자동 로그인·로그아웃·자격 만료 모두 `authed` 로 모이므로 여기 한 곳에서만 부른다.
  // 로그인 전 배치(일반/무인 대상 PC)는 첫 화면(`LoginView`)이 자기 상태로 정한다 — 여기서 COMPACT 를 부르면
  // 자식 효과보다 늦게 돌아 무인 대상 PC 의 낮은 배치를 덮어쓴다.
  useEffect(() => {
    if (authed) applyMainLayout(MAIN_LAYOUT.FULL);
  }, [authed]);

  /**
   * 세션을 연다. **클릭 처리 안에서 동기적으로** 불려야 한다(`openInSessionWindow` 의 팝업 규칙 — await 뒤에 열면 차단된다).
   * @param inPageOnly true 면 세션 창을 쓰지 않고 이 창의 탭으로 연다(자동 접속 — 사용자 클릭이 없다).
   */
  const startSession = useCallback(
    (req: SessionRequest, inPageOnly = false) => {
      setError('');
      setOpenFailure(null);
      // 이 PC 자신의 코드로는 열지 않는다(2026-09-29) — 코드 입력(즉석 접속·연결 화면)이 모두 여기를 지난다.
      // 기기 목록은 agentId(UUID)로 접속하므로 목록 행에서 따로 막는다(`DevicesView`). 비교는 코드 모양일 때만(`deviceCode.ts`).
      if (isSameDeviceCode(req.kind === 'anon' ? req.code : req.target, myDeviceCode)) {
        setError(SELF_CONNECT_BLOCKED);
        return;
      }
      // 넘기기 직전에 로그인 토큰을 새것으로 채운다(`withFreshToken` — 15분 뒤 접속이 Unauthorized 로 실패하던 결함).
      // 창 열기는 기다리지 않는다(팝업 규칙) — 요청 전달만 이 약속을 기다린다.
      const ready = withFreshToken(api, req);
      const onFailure = (failure: SessionOpenFailure, sent: SessionRequest) => {
        setNotice(''); // "열렸습니다" 는 틀린 말이 됐다
        setOpenFailure({ req: sent, failure });
      };
      if (!inPageOnly && openInSessionWindow(ready, onFailure)) {
        setNotice(`${requestLabel(req)} — 원격 세션 창에서 열렸습니다.`);
        return;
      }
      void ready.then((r) => {
        openInPage(r);
        setOverlayHidden(false);
      });
    },
    [api, openInPage, myDeviceCode],
  );

  useEffect(() => {
    if (!notice) {
      return;
    }
    const t = setTimeout(() => setNotice(''), HANDOFF_NOTICE_MS);
    return () => clearTimeout(t);
  }, [notice]);

  // 대상(코드/agentId)에 접속.
  const connect = useCallback(
    // `label` = 세션 창·탭에 보일 이름. 기기 목록은 agentId(UUID)로 접속하므로 이것이 없으면 제목이 날것이 된다.
    (target: string, password?: string, label?: string, opts: ConnectOptions & { inPageOnly?: boolean } = {}) => {
      if (!target.trim()) {
        return;
      }
      startSession(
        {
          kind: 'user',
          target: target.trim(),
          password,
          token: api.token,
          label,
          remember: opts.remember,
          trusted: opts.trusted,
          // 수퍼유저가 조직에 들어가 있으면 그 조직으로 세션을 연다(§14.19.1). 일반 사용자는 빈 값.
          orgContext: api.orgContext || undefined,
        },
        opts.inPageOnly ?? false,
      );
    },
    [api, startSession],
  );

  // 계정 없는 즉석 접속(§17.9.1 Phase E) — 로그인 없이 코드+비밀번호로 접속. 사용 시간 제한이 있다.
  const anonConnect = useCallback(
    (code: string, password: string, inPageOnly = false) => {
      if (!code.trim() || !password) {
        return;
      }
      startSession({ kind: 'anon', code: code.trim(), password }, inPageOnly);
    },
    [startSession],
  );

  const logout = useCallback(async () => {
    // 확인 받고 진행(사용자 요청 2026-09-27) — 로그아웃하면 7일 자동 로그인 보관본도 지워진다.
    const ok = await confirmAction({
      title: '로그아웃할까요?',
      message: '다음 실행 때 자동으로 로그인되지 않습니다. 원격 세션 창에서 진행 중인 세션은 그대로 유지됩니다.',
      confirmLabel: '로그아웃',
    });
    if (!ok) return;
    // 이 창의 세션만 정리한다. 세션 창의 세션은 그 창이 관리한다(로그아웃이 진행 중인 원격지원을 끊지 않게 — TeamViewer 동일).
    closeAllInPage();
    api.logout();
    setAuthed(false);
  }, [api, closeAllInPage]);

  // 자동 로그인·접속(검증/스크린샷용, ?auto=1&loginId=&password=&code=). 사용자 클릭이 없어 팝업을 못 여니 같은 창 탭으로.
  const didAuto = useRef(false);
  useEffect(() => {
    if (didAuto.current) {
      return;
    }
    // 계정 없는 즉석 접속 자동 검증(?anon=1&anonCode=&anonPassword=).
    if (AUTO.anon && AUTO.anonCode && AUTO.anonPassword) {
      didAuto.current = true;
      anonConnect(AUTO.anonCode, AUTO.anonPassword, true);
      return;
    }
    if (!AUTO.auto || !AUTO.loginId) {
      return;
    }
    didAuto.current = true;
    (async () => {
      try {
        await api.login(AUTO.loginId, AUTO.password);
        setAuthed(true);
        if (AUTO.code) {
          connect(AUTO.code, AUTO.sessionpw || undefined, undefined, { inPageOnly: true });
        }
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [api, connect, anonConnect]);

  // 업데이트 확인 — 기동 직후가 아니라 잠시 뒤에 한 번만. 창이 뜨는 경로에 네트워크 대기를 얹지 않는다.
  useEffect(() => {
    const timer = setTimeout(() => {
      void checkForUpdate().then(setUpdate);
    }, UPDATE_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // 로그인하면 내 프로필(역할)을 불러온다. 로그아웃·자격 만료 시 비운다(수퍼유저의 조직 선택도).
  useEffect(() => {
    if (!authed) {
      setMe(null);
      setSuOrgId(SU_ALL_ORGS);
      setSuOrgs([]);
      api.orgContext = '';
      return;
    }
    let alive = true;
    api
      .getMe()
      .then((p) => alive && setMe(p))
      .catch(() => alive && setMe(null));
    return () => {
      alive = false;
    };
  }, [api, authed]);

  // ── 수퍼유저 조직 선택(§14.19.1) ──
  /** 조직 목록(선택 칸용). 조직 화면에서 만들거나 이름을 바꾼 뒤에도 맞도록 선택 칸을 열 때마다 다시 읽는다. */
  const loadSuOrgs = useCallback(() => {
    void api
      .suOrgs()
      .then(setSuOrgs)
      .catch(() => {});
  }, [api]);
  useEffect(() => {
    if (isSu) loadSuOrgs();
  }, [isSu, loadSuOrgs]);

  /**
   * 조직으로 들어가기/나오기. 문맥은 `api.orgContext` 한 곳에 두고(모든 요청이 거기서 머리를 싣는다), 화면은 key 로
   * 통째로 다시 만든다 — 앞 조직의 목록이 잠깐이라도 남아 보이면 "어느 조직을 보고 있나" 를 착각한다.
   */
  const selectSuOrg = useCallback(
    (orgId: string) => {
      api.orgContext = orgId;
      setSuOrgId(orgId);
      setNav(orgId ? 'devices' : 'su-dashboard');
      setError('');
    },
    [api],
  );
  const suOrgName = suOrgs.find((o) => o.orgId === suOrgId)?.orgName ?? '';

  // 지금 역할로 보이는 메뉴. 관리자에서 강등되면 관리 메뉴에 머물러 있지 않게 첫 메뉴로 되돌린다.
  // 수퍼유저가 조직을 고르지 않았으면 su 메뉴(대시보드·조직·서버)만.
  const visibleNav = useMemo<ReadonlyArray<{ key: Nav; label: string; adminLabel?: string; adminOnly?: boolean }>>(
    () => (isSu && !inSuOrg ? SU_NAV_ITEMS : NAV_ITEMS.filter((i) => !i.adminOnly || isAdmin)),
    [inSuOrg, isAdmin, isSu],
  );
  const activeNav: Nav = visibleNav.some((i) => i.key === nav) ? nav : visibleNav[0].key;

  // 목록 화면은 이제 접속 중에도 막지 않는다(세션은 탭에서 진행 — 다른 기기를 이어서 열 수 있어야 한다).
  const content = useMemo(() => {
    switch (activeNav) {
      case 'devices':
        return <DevicesView api={api} onConnect={(t, pw, l, o) => connect(t, pw, l, o)} busy={false} isAdmin={isAdmin} />;
      case 'history':
        return <SessionHistoryView api={api} isAdmin={isAdmin} />;
      case 'users':
        return <UsersView api={api} meId={me?.userId ?? ''} />;
      case 'logs':
        return <AccessLogView api={api} />;
      case 'su-dashboard':
        return <SuDashboardView api={api} onEnterOrg={selectSuOrg} />;
      case 'su-orgs':
        return <SuOrgsView api={api} onEnterOrg={selectSuOrg} />;
      case 'su-server':
        return <SuServerView api={api} />;
      default:
        return <ConnectView onConnect={(t, pw) => connect(t, pw)} busy={false} />;
    }
  }, [activeNav, api, connect, isAdmin, me, selectSuOrg]);

  const inPageCount = inPage.tabs.length;
  // 업데이트 설치(= 앱 재시작)를 막아야 하는가 — 이 창(대체 경로)과 세션 창들의 **진행 중** 세션을 모두 센다
  // (실패·종료 탭은 끊길 세션이 없으니 설치를 막지 않는다).
  const sessionActive = inPage.tabs.filter(isTabLive).length + windowLiveSessions > 0;
  // 대체 경로 세션 탭 화면(이 창을 덮는다) + 접어 둔 동안 되돌아가는 버튼.
  const inPageSessions =
    inPageCount > 0 &&
    (overlayHidden ? (
      <button className="session-pill" onClick={() => setOverlayHidden(false)}>
        원격 세션 {inPageCount}개 보기
      </button>
    ) : (
      <SessionTabs mgr={inPage} layout="overlay" onHide={() => setOverlayHidden(true)} />
    ));
  const noticeLine = notice && <div className="handoff-notice">{notice}</div>;
  // (피제어 채팅은 2026-09-29 부터 **별도 창**이다 — 앱 셸이 `agent-chat.html` 로 띄운다(§9.6.8). 이 창에는 두지 않는다.)
  // 세션 창 넘기기 실패 알림 — 로그인 전(계정 없이 접속)·후 모두 같은 것을 띄운다(화면 위 고정).
  const openFailureAlert = openFailure && (
    <SessionOpenAlert
      label={requestLabel(openFailure.req)}
      failure={openFailure.failure}
      onReopen={async () => {
        const req = await withFreshToken(api, openFailure.req);
        try {
          await reopenInFreshSessionWindow(req);
          setOpenFailure(null);
          setNotice(`${requestLabel(req)} — 새 원격 세션 창에서 열렸습니다.`);
        } catch (f) {
          setOpenFailure({ req, failure: f as SessionOpenFailure });
        }
      }}
      onRetry={() => startSession(openFailure.req)}
      onDismiss={() => setOpenFailure(null)}
    />
  );

  if (!authed) {
    // 미로그인 상태에서도 계정 없는 즉석 접속(§17.9.1)은 가능.
    //
    // ⚠️ 업데이트 배너와 **내 연결 패널**은 로그인 전에도 보여야 한다. 피제어 전용으로 설치한 기기는
    //    계정이 없어 로그인하지 않으므로, 로그인 후 화면에만 두면 그 기기들은 알림도 못 보고
    //    **자기 접속 ID·비밀번호조차 확인할 수 없다**(배너는 검증 준비 중, 패널은 2026-09-08 실사용에서 드러남).
    return (
      <>
        {update && !updateDismissed && (
          <div className="update-banner-floating">
            <UpdateBanner
              update={update}
              sessionActive={sessionActive}
              onDismiss={() => setUpdateDismissed(true)}
            />
          </div>
        )}
        {noticeLine}
        {openFailureAlert}
        {resuming && <div className="handoff-notice">저장된 로그인으로 다시 들어가는 중…</div>}
        <LoginView
          notice={authNotice}
          onLogin={async (email, pw) => {
            await api.login(email, pw);
            saveLastLoginId(email); // 다음 로그인 화면에 채운다(비밀번호는 저장하지 않는다)
            setAuthNotice('');
            setAuthed(true);
          }}
          onAnonConnect={(code, pw) => anonConnect(code, pw)}
          anonBusy={false}
          anonError={error}
        />
        {inPageSessions}
      </>
    );
  }

  return (
    <>
      <div className="shell">
        <aside className="sidebar">
          <div className="title">RemoteSupport</div>
          {/* 수퍼유저 조직 선택(§14.19.1) — "전체" = su 화면, 조직 = 그 조직 관리자 화면 */}
          {isSu && (
            <label className="su-org-picker">
              <span className="hint">조직</span>
              <select value={suOrgId} onFocus={loadSuOrgs} onChange={(e) => selectSuOrg(e.target.value)} aria-label="조직 선택">
                <option value={SU_ALL_ORGS}>전체 (수퍼유저)</option>
                {suOrgs.map((o) => (
                  <option key={o.orgId} value={o.orgId}>
                    {o.orgName}
                    {o.status !== 'active' ? ' (정지)' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          {visibleNav.map((item, i) => (
            <Fragment key={item.key}>
              {/* 관리자 메뉴 묶음이 시작되는 자리에 머리글 — 일상 메뉴와 관리 메뉴를 눈으로 가른다. */}
              {item.adminOnly && !visibleNav[i - 1]?.adminOnly && (
                <div className="nav-section">{ADMIN_SECTION_LABEL}</div>
              )}
              <div
                className={`nav ${activeNav === item.key ? 'active' : ''}`}
                onClick={() => setNav(item.key)}
              >
                {isAdmin && item.adminLabel ? item.adminLabel : item.label}
              </div>
            </Fragment>
          ))}
          <div className="spacer" />
          {me && (
            // 이름을 누르면 [내 계정](이메일·비밀번호 변경, 2026-10-23 사용자 요청).
            <button className="who" title="내 계정 — 이메일·비밀번호 변경" onClick={() => setAccountOpen(true)}>
              {me.userName} · {ROLE_LABEL[me.role] ?? me.role}
            </button>
          )}
          {accountOpen && me && (
            <MyAccountDialog api={api} me={me} onProfileChanged={setMe} onClose={() => setAccountOpen(false)} />
          )}
          <button className="ghost" onClick={() => void logout()}>
            로그아웃
          </button>
        </aside>
        <main className="main">
          {update && !updateDismissed && (
            <UpdateBanner
              update={update}
              sessionActive={sessionActive}
              onDismiss={() => setUpdateDismissed(true)}
            />
          )}
          {noticeLine}
          {/* macOS 권한 경고 — [연결] 화면은 "내 기기 정보" 카드에 같은 경고가 있어 겹치지 않게 뺀다(2026-10-12).
              🔴 `nav` 가 아니라 **실제로 보이는** `activeNav` 로 판정 — 수퍼유저는 `nav` 가 처음 값 'connect' 로 남은 채
              대시보드가 보여, `nav` 로 보면 바로 그 화면(사용자 지적 상황)에서 띠가 숨었다(헤드리스에서 잡음). */}
          {activeNav !== 'connect' && <PermissionBanner />}
          {inSuOrg && (
            <div className="su-context-bar">
              <span>
                수퍼유저로 <b>{suOrgName || '선택한 조직'}</b> 조직을 관리 중입니다. 여기서 한 일은 이 조직 감사 로그에 남습니다.
              </span>
              <button className="ghost" onClick={() => selectSuOrg(SU_ALL_ORGS)}>
                전체로
              </button>
            </div>
          )}
          {error && (
            <div className="row" style={{ marginBottom: 12 }}>
              <span className="hint" style={{ color: 'var(--err)' }}>{error}</span>
            </div>
          )}
          {/* 조직을 바꾸면 화면을 새로 만든다(앞 조직 목록이 남아 보이지 않게). */}
          <Fragment key={suOrgId}>{content}</Fragment>
        </main>
      </div>
      {inPageSessions}
      {openFailureAlert}
    </>
  );
}
